import { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { useNavigate } from 'react-router-dom';

import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import StatsCards from '../components/StatsCards';
import MedicineTable from '../components/MedicineTable';

function AdminDashboard() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [medicines, setMedicines] = useState([]);

  useEffect(() => {
    const initDashboard = async () => {
      try {
        // Auth check
        const {
          data: { user },
        } = await supabase.auth.getUser();

        if (!user) {
          navigate('/admin/login');
          return;
        }

        // Admin check
        const { data: admin } = await supabase
          .from('admins')
          .select('*')
          .eq('id', user.id)
          .single();

        if (!admin) {
          navigate('/');
          return;
        }

        // Fetch medicines
        const { data: medicinesData } = await supabase
          .from('medicines')
          .select(
            'id, brand_name, drug_name, batch_no, manufacturer, mrp, qr_file_path',
          );

        // Scan counts
        const { data: scanCountsData } = await supabase.rpc(
          'get_medicine_scan_counts',
        );

        const scanMap = {};
        scanCountsData?.forEach((row) => {
          scanMap[row.medicine_id] = row.total_scans;
        });

        const merged = medicinesData.map((med) => ({
          ...med,
          total_scans: scanMap[med.id] || 0,
        }));

        setMedicines(merged);
      } catch (err) {
        console.error('Dashboard error:', err);
      } finally {
        setLoading(false);
      }
    };

    initDashboard();
  }, [navigate]);

  const downloadQR = async (fileName) => {
    const { data } = supabase.storage.from('qr-codes').getPublicUrl(fileName);

    const svgUrl = data.publicUrl;

    // Fetch SVG file
    const response = await fetch(svgUrl);
    const svgText = await response.text();

    // Download SVG
    const svgBlob = new Blob([svgText], { type: 'image/svg+xml' });

    const svgLink = document.createElement('a');
    svgLink.href = URL.createObjectURL(svgBlob);
    svgLink.download = fileName;
    document.body.appendChild(svgLink);
    svgLink.click();
    document.body.removeChild(svgLink);

    // Convert SVG → PNG safely
    const img = new Image();
    const svgBase64 = 'data:image/svg+xml;base64,' + btoa(svgText);

    img.src = svgBase64;

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 1000;
      canvas.height = 1000;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);

      canvas.toBlob((blob) => {
        const pngLink = document.createElement('a');
        pngLink.href = URL.createObjectURL(blob);
        pngLink.download = fileName.replace('.svg', '.png');

        document.body.appendChild(pngLink);
        pngLink.click();
        document.body.removeChild(pngLink);
      });
    };
  };

  if (loading) {
    return <div className="p-10">Loading dashboard...</div>;
  }

  return (
    <div className="flex bg-gray-50 min-h-screen">
      <Sidebar />

      <div className="flex-1 p-8">
        <Header />

        <StatsCards medicines={medicines} />

        <MedicineTable medicines={medicines} downloadQR={downloadQR} />
      </div>
    </div>
  );
}

export default AdminDashboard;
